import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  DevicePhoneMobileIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const services = [
  {
    title: "Contact",
    description:
      "Connect with us for personalized support and business inquiries. We are here to help you grow.",
    icon: ChatBubbleLeftRightIcon,
    color: "from-blue-500 to-cyan-500",
    details: [
      "Direct communication with our team",
      "Quick response to your queries",
      "Tailored solutions for your needs",
      "Transparent and open dialogue",
    ],
    more: "Reach out to us anytime for expert advice and partnership opportunities.",
  },
  {
    title: "Email",
    description:
      "Professional email marketing campaigns that engage and convert your audience.",
    icon: DevicePhoneMobileIcon,
    color: "from-purple-500 to-pink-500",
    details: [
      "Targeted email campaigns",
      "Personalized messaging",
      "Automated follow-ups",
      "Performance analytics",
    ],
    more: "Boost your business with effective, data-driven email marketing.",
  },
  {
    title: "Digital",
    description:
      "Comprehensive digital strategies to elevate your brand and drive results online.",
    icon: ChartBarIcon,
    color: "from-green-500 to-emerald-500",
    details: [
      "Digital strategy consulting",
      "Brand positioning",
      "Online reputation management",
      "Growth-focused campaigns",
    ],
    more: "Let us help you navigate the digital landscape and achieve your goals.",
  },
  {
    title: "Content",
    description:
      "Creative content solutions that tell your story and engage your audience.",
    icon: DocumentTextIcon,
    color: "from-orange-500 to-red-500",
    details: [
      "Blog and article writing",
      "Visual storytelling",
      "Content planning and strategy",
      "SEO-optimized materials",
    ],
    more: "We craft compelling content that builds trust and drives action.",
  },
];

const Services = () => {
  const [flipped, setFlipped] = useState(null);
  const navigate = useNavigate();

  // Prevent background scroll when a card is expanded
  useEffect(() => {
    if (flipped !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [flipped]);

  return (
    <section id="services" className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-800 sm:text-4xl mb-4">
            Our Services
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Comprehensive digital marketing solutions to help your business grow
          </p>
        </div>

        {/* Services Cards */}
        <div
          className={`mt-20 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-2 ${
            flipped !== null
              ? "filter blur-sm pointer-events-none select-none"
              : ""
          }`}
        >
          {services.map((service, index) => (
            <div
              key={service.title}
              className="relative flex items-center justify-center h-[400px] group overflow-hidden rounded-2xl shadow-xl border border-gray-200 bg-white"
              onClick={() => setFlipped(index)}
            >
              <div className="w-full h-full rounded-2xl z-20 relative flex flex-col items-center justify-center p-10">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center mb-8 shadow-md border border-gray-300">
                  <service.icon className="w-10 h-10 text-gray-800" />
                </div>
                <h3 className="mt-2 text-xl font-bold text-gray-800 mb-3 text-center font-poppins">
                  {service.title}
                </h3>
                <p className="text-sm text-gray-600 text-center mb-8 font-medium leading-relaxed max-w-xs mx-auto">
                  {service.description}
                </p>
                <button
                  className="mt-auto px-7 py-3 bg-gray-800 text-white rounded-xl shadow hover:bg-gray-700 font-semibold text-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFlipped(index);
                  }}
                >
                  Learn more
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Expanded Card */}
        {typeof window !== "undefined" &&
          flipped !== null &&
          createPortal(
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            >
              <div className="relative rounded-3xl shadow-2xl flex flex-col items-center justify-center w-full max-w-lg mx-4 p-10 bg-white border border-gray-200">
                {/* Close Button */}
                <button
                  className="absolute top-4 right-4 p-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-800"
                  onClick={() => setFlipped(null)}
                  aria-label="Close"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>

                {/* Service Details */}
                <div
                  className={`w-20 h-20 bg-gradient-to-br ${services[flipped].color} rounded-2xl flex items-center justify-center mb-6 shadow-md`}
                >
                  {/* <services[flipped].icon className="w-10 h-10 text-gray-800" /> */}
                </div>
                <h3 className="text-3xl font-extrabold text-gray-800 mb-4 text-center">
                  {services[flipped].title}
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4 text-left w-full max-w-xs mx-auto text-lg">
                  {services[flipped].details.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
                <p className="text-gray-600 text-center mb-6 text-lg">
                  {services[flipped].more}
                </p>
              </div>
            </div>,
            document.body
          )}
      </div>
    </section>
  );
};

export default Services;
